<template>
	<div class="edit-mission-container">
		<Header :title="isEditing ? 'Edit Mission' : 'New Mission'" :user="currentUser" />

		<main class="page-main">
			<form @submit.prevent="saveMission" class="mission-form">
								<div class="">
									<!-- Dropdown for assigning mission to a child -->
									<div class="form-group">
										<label for="child-select" style="font-weight:500;margin-bottom:0.5rem;display:block;">Assign to Child:</label>
										<select id="child-select" v-model="missionUserId" style="width:100%;padding:0.5rem;">
											<option value="">-- Select Child --</option>
																	<option v-for="child in familyChildren" :key="child.id" :value="child.id">
																		{{ child.name }}
																	</option>
										</select>
									</div>
					<div v-if="!editingMissionName" class="name-display" @click="startEditingMissionName">
						{{ mission.name || 'mission' }}
					</div>
					<input v-else id="mission-name-input" class="name-input" v-model="mission.name" type="text" required
						placeholder="mission" @blur="finishEditingMissionName"
						@keydown.enter="finishEditingMissionName" />
				</div>

				<div class="">
					<div class="time-setter">
						<div class="time-display-formatted">{{ formatTimeDisplay(totalMinutes, totalSeconds) }}</div>
						<div class="time-controls">
							<div class="time-control">
								<div class="time-buttons">
									<button type="button" @click="adjustTotalMinutes(-1)" class="btn">-M</button>
									<button type="button" @click="adjustTotalMinutes(1)" class="btn">+M</button>
								</div>
							</div>
							<div class="time-control">
								<div class="time-buttons">
									<button type="button" @click="adjustTotalSeconds(-15)" class="btn">-S</button>
									<button type="button" @click="adjustTotalSeconds(15)" class="btn">+S</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="">	
					<div class="action-btn-bar">
						<div class="steps-title">Steps</div>
					</div>

					<div v-if="mission.steps.length === 0" class="no-steps">
						No steps added yet
					</div>

					<div v-else class="steps-list">
						<div v-for="(step, index) in mission.steps" :key="step.id" class="step-item step-column">
							<div class="step-name-row">
								<template v-if="editingStepId !== step.id">
									<div class="name-display" @click="startEditingStep(step.id)">
										{{ step.name || 'step' }}
									</div>
								</template>
								<template v-else>
									<input :id="`name-${step.id}`" v-model="step.name" type="text"
										placeholder="step" class="name-input"
										@blur="finishEditingStep" @keydown.enter="finishEditingStep"
										@keydown.escape="finishEditingStep" />
								</template>
							</div>
							<div class="step-controls-row">
								<button type="button" @click="moveStepUp(index)" :disabled="index === 0" class="btn">▲</button>
								<button type="button" @click="moveStepDown(index)" :disabled="index === mission.steps.length - 1" class="btn">▼</button>
								<button type="button" @click="removeStep(index)" class="btn">X</button>
							</div>
						</div>
					</div>
				</div>

				<div class="action-btn-bar">
					<button type="button" @click="goBack" class="action-btn">Cancel</button>
					<button type="button" @click="addStep" class="action-btn construct-btn">+Step</button>

					<button type="submit" :disabled="saving" class="action-btn construct-btn">
						{{ saving ? 'Saving...' : 'Save' }}
					</button>
				</div>
			</form>
		</main>
	</div>
</template>

<script setup lang="ts">
const missionUserId = ref('')
const familyChildren = ref<User[]>([])
import Header from '../components/Header.vue'
import { ref, reactive, onMounted, computed, nextTick, type Ref } from 'vue'
import { dbService, type Mission, type MissionStep, type User } from '../services/database'

const currentUser: Ref<User|null> = ref(null)
onMounted(async () => {
	// Load user data from sessionStorage (no session validation needed)
	try {
		const userStr = sessionStorage.getItem('currentUser');
		if (userStr) {
			currentUser.value = JSON.parse(userStr);
			console.log('EditMission: User loaded from sessionStorage:', currentUser.value);
		} else {
			console.log('EditMission: No user data in sessionStorage');
		}
	} catch (error) {
		console.error('EditMission: Error parsing user data from sessionStorage:', error);
	}
	
	console.log('EditMission: Loading without session validation');
	
	window.scrollTo({ top: 0, behavior: 'smooth' });
})

import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()

const isEditing = computed(() => !!route.params.id)
const saving = ref(false)

// editing flags declared early so they're available during initial render
const editingStepId = ref<string | null>(null)
const editingMissionName = ref(false)

const mission = reactive({
	name: 'New Mission',
	totalTime: 0,
	steps: [] as MissionStep[],
	childId: ''
})

const totalMinutes = ref(1) // Start at 1 minute by default
const totalSeconds = ref(0)

const startEditingMissionName = async () => {
	editingMissionName.value = true;
	await nextTick();
	window.scrollTo({ top: 0, behavior: 'smooth' });
	const input = document.querySelector('#mission-name-input') as HTMLInputElement | null;
	if (input) {
		input.focus();
		input.select();
	}
}

const finishEditingMissionName = () => {
	editingMissionName.value = false
}

const formatTimeDisplay = (minutes: number, seconds: number): string => {
	return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

const createCleanStep = (id: string, name: string): MissionStep => {
	return {
		id: id,
		name: name
	}
}

const startEditingStep = async (stepId: string) => {
	editingStepId.value = stepId;
	await nextTick();
	// Try both id formats for input selection
	const input = document.querySelector(`#name-${stepId}`) as HTMLInputElement || document.querySelector(`#step-name-${stepId}`) as HTMLInputElement;
	if (input) {
		input.focus();
		input.select();
	}
}

const finishEditingStep = () => {
	editingStepId.value = null
}

const updateTotalTime = () => {
	mission.totalTime = (totalMinutes.value || 0) * 60 + (totalSeconds.value || 0)
}

const adjustTotalMinutes = (delta: number) => {
	totalMinutes.value = Math.max(0, (totalMinutes.value || 0) + delta)
	updateTotalTime()
}

const adjustTotalSeconds = (delta: number) => {
	const newSeconds = (totalSeconds.value || 0) + delta
	if (newSeconds >= 60) {
		totalMinutes.value = (totalMinutes.value || 0) + Math.floor(newSeconds / 60)
		totalSeconds.value = newSeconds % 60
	} else if (newSeconds < 0) {
		if (totalMinutes.value > 0) {
			totalMinutes.value = totalMinutes.value - 1
			totalSeconds.value = 60 + newSeconds
		} else {
			totalSeconds.value = 0
		}
	} else {
		totalSeconds.value = newSeconds
	}
	updateTotalTime()
}


const addStep = async () => {
	const stepId = crypto.randomUUID();
	const stepIndex = mission.steps.length;
	const newStep = createCleanStep(stepId, `Step ${stepIndex + 1}`);

	mission.steps.push(newStep);
	// per-step time UI removed; steps do not store individual time values

	// Start editing the newly created step
	await nextTick();
	startEditingStep(stepId);
}

const removeStep = (index: number) => {
	const step = mission.steps[index]
	if (step) {
		// no per-step time state to clean up
	}
	mission.steps.splice(index, 1)
}

const swapSteps = (i: number, j: number) => {
	if (i < 0 || j < 0 || i >= mission.steps.length || j >= mission.steps.length) return
	const stepA = mission.steps[i]
	const stepB = mission.steps[j]
	if (!stepA || !stepB) return

	const idA = stepA.id
	const idB = stepB.id

	// swap steps in array
	const tmp = stepA
	mission.steps[i] = stepB
	mission.steps[j] = tmp

	// no per-step time mappings to swap
}

const moveStepUp = (index: number) => {
	if (index <= 0) return
	swapSteps(index, index - 1)
}

const moveStepDown = (index: number) => {
	if (index >= mission.steps.length - 1) return
	swapSteps(index, index + 1)
}

const goBack = () => {
	router.push('/missions')
}

const goDashboard = () => {
	router.push('/dashboard')
}

const saveMission = async () => {
	saving.value = true

	try {
		// Validate required fields
		if (!mission.name.trim()) {
			alert('Mission name is required')
			return
		}

		if (mission.totalTime <= 0) {
			alert('Total time must be greater than 0')
			return
		}

		// Ensure database is initialized


		// Clean and serialize steps to ensure they can be cloned by IndexedDB
		const cleanSteps = mission.steps.map((step: MissionStep) => ({
			id: step.id,
			name: step.name
		}))

		// Always use missionUserId from dropdown for saving
		const userIdToSave = missionUserId.value || (route.query.childId ? String(route.query.childId) : '')

		if (isEditing.value) {
			const existingMission = await dbService.getMission(route.params.id as string)
			if (existingMission) {
				// Serialize steps and totalTime into data field for update
				const updatedMission: any = {
					id: existingMission.id,
					name: mission.name,
					user_id: userIdToSave,
					data: JSON.stringify({
						totalTime: mission.totalTime,
						steps: cleanSteps
					})
				};
				await dbService.updateMission(updatedMission);
			}
		} else {
			// Only send user_id and account_id if they are valid UUIDs
			const payload: Record<string, any> = {
				name: mission.name,
				user_id: userIdToSave,
				data: JSON.stringify({
					totalTime: mission.totalTime,
					steps: cleanSteps
				})
			};
			if (currentUser.value?.id && /^[0-9a-fA-F-]{36}$/.test(currentUser.value.id)) {
				payload.account_id = currentUser.value.id;
			}
			await dbService.addMission(payload);
		}

		router.push('/missions')
	} catch (error) {
		console.error('Failed to save mission:', error)
		console.error('Mission data:', {
			name: mission.name,
			totalTime: mission.totalTime,
			steps: mission.steps
		})
		alert('Failed to save mission: ' + (error instanceof Error ? error.message : 'Unknown error'))
	} finally {
		saving.value = false
	}
}

const loadMission = async () => {
	if (!isEditing.value) {
		// Set default time for new mission
		totalMinutes.value = 1
		totalSeconds.value = 0
		updateTotalTime()
		return
	}

	try {
		const existingMission = await dbService.getMission(route.params.id as string)
		if (existingMission) {
			mission.name = existingMission.name;
			let parsedData: any = {};
			if (existingMission.data) {
				try {
					parsedData = typeof existingMission.data === 'string' ? JSON.parse(existingMission.data) : existingMission.data;
				} catch (e) {
					parsedData = {};
				}
			}
			mission.totalTime = typeof parsedData.totalTime === 'number' && !isNaN(parsedData.totalTime)
				? parsedData.totalTime
				: 60;
			// Set dropdown to user_id from loaded mission
			missionUserId.value = existingMission.user_id || '';
			mission.steps = Array.isArray(parsedData.steps)
				? parsedData.steps.map((step: MissionStep) => createCleanStep(step.id, step.name))
				: [];
			totalMinutes.value = Math.floor(mission.totalTime / 60);
			totalSeconds.value = mission.totalTime % 60;
		}
	} catch (error) {
		console.error('Failed to load mission:', error)
	}
}

onMounted(async () => {
	await loadMission()
})
</script>

<style scoped>
/* Add extra space below the main action button bar */
.action-btn-bar {
	margin-bottom: 4rem;
}
/* Step controls bottom row */
.step-controls-bottom {
	display: flex;
	flex-direction: row;
	gap: 0.5rem;
	justify-content: flex-start;
	margin-top: 1rem;
}
/* Use the same styles as EditActivity.vue, but rename .edit-activity-container, .activity-form, .activity-name-display, etc. to .edit-mission-container, .mission-form, .mission-name-display, etc. */
.edit-mission-container {
	min-height: 100vh;
	background-color: #f5f5f5;
	padding-bottom: 5rem;
}

.page-header {
	background: white;
	padding: 1rem 2rem;
	border-bottom: 1px solid #ddd;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.page-header h1 {
	margin: 0;
	color: #333;
}

.edit-mission-main {
	padding: 1rem;
}

.mission-form {
	max-width: 600px;
	background: white;
	padding: 1rem;
	border-radius: 8px;
	border: 1px solid #ddd;
}






.time-setter {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	align-items: center;
}

.time-display-formatted {
	font-size: 5rem;
	font-weight: bold;
	color: #333;
	font-family: monospace;
	text-align: center;
	margin-bottom: 1rem;
}

.time-controls {
	display: flex;
	gap: 2rem;
	justify-content: center;
}

.time-control {
	text-align: center;
}

.time-control label {
	font-size: 0.9rem;
	color: #666;
	display: block;
	margin-bottom: 0.5rem;
}

.time-buttons {
	display: flex;
	gap: 0.5rem;
	justify-content: center;
}



.steps-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 1rem;
}

.add-step-btn {
	padding: 0.5rem 1rem;
	background-color: #666;
	color: white;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	font-size: 0.9rem;
}

.no-steps {
	text-align: center;
	padding: 2rem;
	color: #666;
	background-color: #f8f8f8;
	border: 1px solid #eee;
	border-radius: 4px;
}

.steps-list {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.step-item.step-column {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	padding: 2rem 1rem;
	background-color: #f8f8f8;
	border: 1px solid #eee;
	border-radius: 4px;
	position: relative;
	gap: 0.75rem;
	margin-bottom: 2rem;
}
.step-name-row {
	width: 100%;
	margin-bottom: 0.5rem;
}
.step-controls-row {
	display: flex;
	flex-direction: row;
	gap: 0.5rem;
	justify-content: flex-start;
	margin-top: 0.5rem;
}

.step-left {
	flex: 1;
	min-width: 0;
}

.step-right {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 0.5rem;
	width: 72px;
	flex-shrink: 0;
}

.step-header {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	/* tightened so name gets more room */
	margin-bottom: 0.5rem;
}

/* step-number removed to give more room for the step name */

.step-name-container {
	flex: 1;
	min-width: 0;
}

.step-time-section {
	position: static;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 0.5rem;
	margin-top: 0.25rem;
}

.step-time-display-formatted {
	font-size: 5rem;
	font-weight: 700;
	color: #333;
	font-family: monospace;
	text-align: left;
	line-height: 1.1;
}

.step-time-top {
	margin-bottom: 6px;
}

.name-display {
	font-size: 1.4rem;
	font-weight: 600;
	color: #333;	
	padding: 0.5rem;
	cursor: pointer;
	min-height: 2rem;
	display: flex;
	align-items: center;
	border:none;
	transition: background-color 0.2s ease;
	width: 100%;
}

.name-input {
	width: 100%;
	font-size: 1.4rem;
	font-weight: 600;
	color: #333;
	padding: 0.5rem;
	border:none;
	background: white;
	outline: none;
}

.step-time-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(40px, 1fr));
	gap: 8px;
	width: 100%;
}

.step-controls {
	display: flex;
	flex-direction: column;
	gap: 8px;
	align-items: stretch;
	width: 100%;
}

.step-time-controls {
	display: flex;
	gap: 0.5rem;
	justify-content: flex-end;
}

.step-time-controls-vertical {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	justify-content: flex-end;
}

.step-time-buttons-vertical {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.step-order-controls {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.btn {
	padding: 0.5rem 0.75rem;
	background-color: #666;
	color: white;
	border: none;
	border-radius: 6px;
	cursor: pointer;
	font-size: 0.9rem;
	width: 100%;
	border: none;
	cursor: pointer;
	font-size: 1.2rem;
	font-weight: bold;
	min-width: 60px;
}


.btn:disabled {
	background-color: #ccc;
	cursor: not-allowed;
	opacity: 0.8;
}

.step-time-control {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
}

.step-time-control label {
	font-size: 0.8rem;
	color: #666;
	margin-bottom: 0.25rem;
}

.step-time-buttons {
	display: flex;
	gap: 0.25rem;
}




/* Logo button positioning for EditMission header */
.edit-mission-container .page-header {
	position: relative;
	padding-left: 4.75rem;
	/* space for larger logo */
}

.edit-mission-container .logo-btn {
	position: absolute;
	left: 0.5rem;
	top: 50%;
	transform: translateY(-50%);
	background: transparent;
	border: none;
	padding: 0;
	display: flex;
	align-items: center;
	cursor: pointer;
}

.edit-mission-container .logo-btn img {
	width: 56px;
	height: 56px;
	object-fit: contain;
	display: block;
}
</style>